const SimplexNoise = require('./simplexNoise.js')

/*
* File Name / heySiri.js
* Created Date / Sep 17, 2020
* Aurhor / Toshiya Marukubo
* Twitter / https://twitter.com/toshiyamarukubo
*/


/*
  Common Tool.
*/

class Tool {
  // random number.
  static randomNumber(min, max) {
    return Math.floor(Math.random() * (max - min + 1) + min);
  }
  // random color rgb.
  static randomColorRGB() {
    return (
      "rgb(" +
      this.randomNumber(0, 255) +
      ", " +
      this.randomNumber(0, 255) +
      ", " +
      this.randomNumber(0, 255) +
      ")"
    );
  }
  // random color hsl.
  static randomColorHSL(hue, saturation, lightness) {
    return (
      "hsl(" +
      hue +
      ", " +
      saturation +
      "%, " +
      lightness +
      "%)"
    );
  }
  // gradient color.
  static gradientColor(ctx, cr, cg, cb, ca, x, y, r) {
    const col = cr + "," + cg + "," + cb;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, "rgba(" + col + ", " + (ca * 1) + ")");
    g.addColorStop(0.5, "rgba(" + col + ", " + (ca * 0.5) + ")");
    g.addColorStop(1, "rgba(" + col + ", " + (ca * 0) + ")");
    return g;
  }
}

/*
  When want to use angle.
*/

class Angle {
  constructor(angle) {
    this.a = angle;
    this.rad = this.a * Math.PI / 180;
  }

  incDec(num) {
    this.a += num;
    this.rad = this.a * Math.PI / 180;
    return this.rad;
  }
}

/*
  When want to use controller.
*/

class Controller {
  constructor(id) {
    this.id = document.getElementById(id);
  }
  getVal() {
    return this.id.value;
  }
}

/*
  When want to use time.
*/

class Time {
  constructor(time) {
    this.startTime = time;
    this.lastTime;
    this.elapsedTime;
  }

  getElapsedTime() {
    this.lastTime = Date.now();
    this.elapsedTime = (this.startTime - this.lastTime) * -1;
    return this.elapsedTime;
  }
}

/*
  When want to use time.
*/
let canvas;
const simplex = new SimplexNoise();

export class Canvas {
  constructor(dom, width, height) {
    // create canvas.
    canvas = document.createElement("canvas");
    // if on screen.
    dom.appendChild(canvas);
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.width = canvas.width = width;
    this.height = canvas.height = height;
    // mouse infomation.
    this.mouseX = null;
    this.mouseY = null;
    // shape
    this.shapeNum = [
      {
        r: 255,
        g: 0,
        b: 60,
        a: 1
      },
      {
        r: 0,
        g: 255,
        b: 219,
        a: 0.95
      },
      {
        r: 0,
        g: 30,
        b: 255,
        a: 1
      },
      // {
      //   r: 255,
      //   g: 246,
      //   b: 0,
      //   a: 1
      // }
    ];
    this.shapes = [];
    // glitch
    this.glitch;
    this.glitchSwitch = false;
  }

  // init, render, resize
  init() {
    if (!this.$init)
      this.$init = true;
    this.glitchSwitch = false;
    this.shapes = [];
    for (let i = 0; i < this.shapeNum.length; i++) {
      const s = new Shape(this.ctx, 0, this.height / 2, i + 1, this.shapeNum[i]);
      this.shapes.push(s);
    }
    this.glitch = new Glitch(this.ctx);
  }

  render() {
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (let i = 0; i < this.shapes.length; i++) {
      this.shapes[i].render(i);
    }
    this.cancelAnimationFrame()
    this.requestAnimationFrameIndex = window.requestAnimationFrame(() => {
      this.render();
    });
  }

  start() {
    canvas.behavior = 0;
    this.render();
  }

  loading() {
    canvas.behavior = 1;
  }

  stop() {
    canvas.behavior = 3
    setTimeout(() => {
      this.ctx.clearRect(0, 0, canvas.width, canvas.height);
      this.cancelAnimationFrame()
    }, 500)
  }

  cancelAnimationFrame() {
    if (this.requestAnimationFrameIndex) {
      window.cancelAnimationFrame(this.requestAnimationFrameIndex)
      this.requestAnimationFrameIndex = null
    }
  }

  resize() {
    this.width = canvas.width = window.innerWidth;
    this.height = canvas.height = window.innerHeight;
    this.init();
  }
}

/*
  Shape class.
*/

class Shape {
  constructor(ctx, x, y, i, c) {
    this.ctx = ctx;
    this.init(x, y, i, c);
  }

  init(x, y, i, c) {
    this.x = x;
    this.y = y;
    this.i = i;
    this.cr = 50;
    this.a = new Angle(Tool.randomNumber(0, 360));
    this.a1 = new Angle(Tool.randomNumber(0, 360));
    this.step = canvas.width / 360;
    this.c = c || {
      r: Tool.randomNumber(0, 255),
      g: Tool.randomNumber(0, 255),
      b: Tool.randomNumber(0, 255),
      a: 1
    };
    this.noiseDist = 10;
    this.noiseX = 1000;
    this.noiseY = 1000;
  }

  drawLine() {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    // ctx.globalCompositeOperation = 'xor';
    ctx.lineJoin = 'bevel';
    ctx.fillStyle = `rgba(${this.c.r},${this.c.g},${this.c.b},${this.c.a})`;
    ctx.beginPath();
    ctx.moveTo(this.x, this.y);
    for (let x = 0; x <= canvas.width; x += this.step) {
      const noise = simplex.noise3D(x / this.noiseX, this.y / this.noiseY, this.a.rad) * this.noiseDist;
      const y = Math.sin(x * Math.PI / 180 / 2) * noise + this.y;
      if (x > canvas.width) {
        ctx.lineTo(x * this.step, canvas.height / 2);
      } else {
        ctx.lineTo(x * this.step, y);
      }
    }
    for (let x = canvas.width; x > 0; x -= this.step) {
      const noise = simplex.noise3D(x / this.noiseX, this.y / this.noiseY, this.a.rad) * -this.noiseDist;
      const y = Math.sin(x * Math.PI / 180 / 2) * noise + this.y;
      if (x > canvas.width) {
        ctx.lineTo(x * this.step, canvas.height / 2);
      } else {
        ctx.lineTo(x * this.step, y);
      }
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  drawCircle() {
    const ctx = this.ctx;
    // in
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = Tool.gradientColor(ctx, this.c.r, this.c.g, this.c.b, this.c.a, canvas.width / 2, this.y, this.cr);
    ctx.translate(canvas.width / 2, this.y);
    ctx.rotate(Math.sin(this.a1.rad));
    ctx.scale(Math.cos(this.a1.rad * this.i), Math.sin(this.a1.rad * this.i));
    ctx.translate(-canvas.width / 2, -this.y);
    ctx.beginPath();
    ctx.arc(canvas.width / 2, this.y, this.cr, 0, Math.PI * 2, false);
    ctx.fill();
    ctx.restore();
    // out
    ctx.save();
    ctx.lineWidth = 5;
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = Tool.gradientColor(ctx, this.c.r, this.c.g, this.c.b, this.c.a, canvas.width / 2, this.y, this.cr);
    ctx.strokeStyle = Tool.gradientColor(ctx, this.c.r, this.c.g, this.c.b, this.c.a, canvas.width / 2, this.y, this.cr + 5);
    ctx.beginPath();
    ctx.arc(canvas.width / 2, this.y, this.cr, 0, Math.PI * 2, false);
    ctx.fill();
    ctx.stroke();
    // click
    if (ctx.isPointInPath(canvas.mouseX, canvas.mouseY)) {
      canvas.init();
      this.cr = 50;
      canvas.mouseX = null;
      canvas.mouseY = null;
    }
    ctx.restore();
  }

  changeParams() {
    if (canvas.behavior === 1) {
      this.noiseDist = 30;
      this.noiseX = 100;
      this.noiseY = 100;
    }
    if (canvas.behavior === 2) {
      this.noiseDist *= 1.05;
      this.noiseX *= 1.1;
      this.noiseY *= 1.1;
    }
    if (canvas.behavior === 3) {
      this.noiseDist *= 0.9;
      this.noiseX *= 0.9;
      this.noiseY *= 0.9;
    }
  }

  updateParams() {
    this.a.incDec(1);
    this.a1.incDec(0.5);
    if (this.cr === 51) {
      canvas.glitchSwitch = false;
      this.c = {
        r: Tool.randomNumber(0, 255),
        g: Tool.randomNumber(0, 255),
        b: Tool.randomNumber(0, 255),
        a: 1
      };
    }
    if (this.cr > 50) this.cr -= 1;
  }

  render() {
    this.updateParams();
    this.changeParams();
    this.drawLine()
  }
}

/*
  Glitch class.
*/

class Glitch {
  constructor(ctx) {
    this.ctx = ctx;
    this.splitNum = 5;
    this.splitY = canvas.height / this.splitNum;
    this.angles = [];
    this.anglesForRGB = [];
    this.dataArr = [];
    this.getAngles();
    this.getAnglesForRGB();
  }

  getAnglesForRGB() {
    for (let i = 0; i < 3; i++) {
      const a = Tool.randomNumber(0, 360);
      const r = a * Math.PI / 180;
      const arr = [a, r];
      this.anglesForRGB.push(arr);
    }
  }

  getAngles() {
    for (var i = 0; i < this.splitNum; i++) {
      const angle = Tool.randomNumber(0, 360);
      const gap = Tool.randomNumber(5, 20);
      const arr = [angle, gap];
      this.angles.push(arr);
    }
  }

  getImageData() {
    for (let i = 0; i < this.splitNum; i++) {
      let d = this.ctx.getImageData(0, this.splitY * i, canvas.width, this.splitY + 1);
      let data = d.data;
      for (let i = 3; i < data.length - 4; i += 4) {
        if (data[i] < 255) {
          data[i] = 50;
          data[i - 1] = Math.sin(this.anglesForRGB[0][1]) * 255;
          data[i - 2] = Math.cos(this.anglesForRGB[1][1]) * 255;
          data[i - 3] = Math.tan(this.anglesForRGB[2][1]) * 255;
        }
      }
      this.dataArr.push(d);
    }
  }

  updateAnglesForRGB() {
    for (let i = 0; i < this.anglesForRGB.length; i++) {
      this.anglesForRGB[i][1] += Tool.randomNumber(-1, 1) * Math.random();
    }
  }

  addImage() {
    for (let i = 0; i < this.splitNum; i++) {
      if (Math.random() > 0.5) {
        this.ctx.putImageData(this.dataArr[i], Math.sin(this.angles[i][0] * Math.PI / 180) * this.angles[i][1], this.splitY * i);
      } else {
        this.ctx.putImageData(this.dataArr[Tool.randomNumber(0, this.splitNum - 1)], Math.sin(this.angles[i][0] * Math.PI / 180) * canvas.width, this.splitY * i);
      }
    }
  }

  changeAngle() {
    for (var i = 0; i < this.angles.length; i++) {
      this.angles[i][0] += Tool.randomNumber(-50, 50);
    }
  }

  render() {
    this.dataArr = [];
    this.getImageData();
    this.changeAngle();
    this.updateAnglesForRGB();
    this.addImage();
  }
}